import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Appointments from "@/pages/Appointments";
import CalendarPage from "@/pages/CalendarPage";
import Customers from "@/pages/Customers";
import Staff from "@/pages/Staff";
import Services from "@/pages/Services";
import Reports from "@/pages/Reports";
import Promotions from "@/pages/Promotions";
import AIAssistant from "@/pages/AIAssistant";
import Settings from "@/pages/Settings";
import Billing from "@/pages/Billing";
import PlatformAdmin from "@/pages/PlatformAdmin";
import SetPassword from "@/pages/SetPassword";
import MarketingLayout from "@/marketing/MarketingLayout";
import Landing from "@/marketing/Landing";
import Pricing from "@/marketing/Pricing";
import PromotionsMarketing from "@/marketing/PromotionsMarketing";
import { I18nProvider } from "@/i18n/I18nContext";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF9]">
        <div className="h-10 w-10 rounded-full border-2 border-[#E5E2DC] border-t-[#2C3E35] animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  return children;
}

function NeedsLogin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to={user.role === "superadmin" ? "/admin" : "/dashboard"} replace />;
  return children;
}

function AdminProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF9]">
        <div className="h-10 w-10 rounded-full border-2 border-[#E5E2DC] border-t-[#2C3E35] animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  if (user.role !== "superadmin") return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public marketing site */}
            <Route element={<MarketingLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/promotions" element={<PromotionsMarketing />} />
            </Route>

            {/* Auth */}
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/set-password" element={<NeedsLogin><SetPassword /></NeedsLogin>} />

            {/* Platform super-admin */}
            <Route path="/admin" element={<AdminProtected><PlatformAdmin /></AdminProtected>} />

            {/* Dashboard app */}
            <Route path="/dashboard" element={<Protected><DashboardLayout /></Protected>}>
              <Route index element={<Dashboard />} />
              <Route path="appointments" element={<Appointments />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="customers" element={<Customers />} />
              <Route path="staff" element={<Staff />} />
              <Route path="services" element={<Services />} />
              <Route path="reports" element={<Reports />} />
              <Route path="promotions" element={<Promotions />} />
              <Route path="billing" element={<Billing />} />
              <Route path="assistant" element={<AIAssistant />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
